import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import Modal from "react-bootstrap/Modal";
import ClientSearch from "../common/ClientSearch";
import FloatingInput from "../common/FloatingInput";
import BedClientHistory from "./BedClientHistory";
import {
  residenceBeds,
  residenceClientAssginment,
  censusServiceConfigs,
} from "../services/soar";
import { addDailyService } from "../rev/RevService";

function ResidenceBeds({ data, setData, updateData }) {
  const [beds, setBeds] = useState([]);
  const [bedData, setBedData] = useState("");
  const [serviceConfigs, setServiceConfigs] = useState([]);
  const [newBedOpen, setNewBedOpen] = useState(false);
  const [newBedAssignmentOpen, setNewBedAssignmentOpen] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [bedStatusOpen, setBedStatusOpen] = useState(false);
  const [bedStatusData, setBedStatusData] = useState({});

  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";

  useEffect(() => {
    if (data && data.beds) {
      setBeds(data.beds);
    }
  }, [data]);

  useEffect(() => {
    if (selectedData.client) {
      getServiceConfigs();
    }
  }, [selectedData.client, selectedData.clientProgramName]);

  const getServiceConfigs = async () => {
    const serviceConfigsResponse = await censusServiceConfigs({
      command: "query",
      application: applicationId,
      keyword: "searchbyprogram",
      searchTerms: {
        program: selectedData?.unitName,
      },
    });

    if (serviceConfigsResponse.code === "success") {
      setServiceConfigs(serviceConfigsResponse.data);
      if (serviceConfigsResponse.data.length === 1) {
        setSelectedData((prev) => ({
          ...prev,
          serviceConfiguration: serviceConfigsResponse.data[0].id,
          serviceConfigurationName: serviceConfigsResponse.data[0].name,
        }));
      }
    } else {
      toast.error("Error loading Service Configurations");
    }
  };

  const addBed = async () => {
    const addBedResponse = await residenceBeds({
      command: bedData.id ? "update" : "insert",
      application: applicationId,
      id: bedData.id ? bedData.id : null,
      residence: data.id,
      name: bedData.id ? bedData.name : `Bed ${(beds.length || 0) + 1}`,
      effective: bedData.effective,
      configuration: {
        gender: bedData.gender,
        ageMin: bedData.ageMin,
        ageMax: bedData.ageMax,
        bedType: bedData.bedType,
        onHoldReason: bedData.onHoldReason || "",
      },
    });

    if (addBedResponse.code === "success") {
      toast.success(`Bed ${bedData.id ? "Updated" : "Created"} Successfully`);
      setBedData({ name: "" });
      setNewBedOpen(false);
      updateData();
    } else {
      toast.error(`Error ${bedData.id ? "Updating" : "Creating"} Bed`);
    }
  };

  const addProgramBed = async () => {
    const bedAssignementResponse = await residenceBeds({
      command: "insert",
      application: applicationId,
      residence: data.id,
      name: `Bed ${(data.beds.length + 1).toString()}`,
      configuration: {},
    });

    if (bedAssignementResponse.code === "success") {
      toast.success("Program Bed Created Successfully");
      updateData();
    } else {
      toast.error("Error Creating Program Bed");
    }
  };

  const toggleBedStatus = async (bed) => {
    const response = await residenceBeds({
      command: bed.expiration ? "unexpire" : "expire",
      expiration: bed.statusDate,
      expirationReason: bed.statusReason || "",
      application: applicationId,
      id: bed.id,
    });

    if (response.code === "success") {
      toast.success(`${bed.active ? "Expired" : "Unexpired"} Bed`);
      updateData();
      setBedStatusOpen(false);
    } else {
      toast.error(
        `Error ${bed.active ? "Expiring" : "Unexpiring"} Bed: ${
          response.message
        }`
      );
    }
  };

  const addBedAssignment = async () => {
    const bedAssignementResponse = await residenceClientAssginment({
      command: "insert",
      application: applicationId,
      residence: data.id,
      bed: selectedData.bed.id,
      client: selectedData.client.contactid,
      clientName: selectedData.client.fullname,
      serviceConfiguration: selectedData.serviceConfiguration,
      clientProgram: selectedData.clientProgram,
      clientProgramName: selectedData.clientProgramName,
      admission: new Date(selectedData.admissionDate),
      program: selectedData.unit,
      programName: selectedData.unitName,
    });
    if (bedAssignementResponse.code === "success") {
      toast.success("Client Admitted to Bed");
      const bedAssignementData = {
        ...selectedData,
        id: bedAssignementResponse.id,
      };
      const dailyServiceResponse = await addDailyService(
        {
          client: {
            id: selectedData.client.contactid,
            name: selectedData.client.fullname,
            dob: selectedData.client.dob,
            mpi: selectedData.client.mpi,
          },
          id: selectedData.clientProgram,
          name: selectedData.clientProgramName,
          unit: {
            id: selectedData.unit,
            name: selectedData.unitName,
          },
        },
        bedAssignementData,
        data.binti[0] ? data.binti[0]?.["application-home-name"] : data.name,
        new Date(selectedData.admissionDate),
        selectedData.serviceStatus
      );
      if (dailyServiceResponse.code === "success") {
        toast.success("Daily Service Added");
      } else {
        toast.error("Error Adding Daily Service");
      }
      updateData();
      setNewBedAssignmentOpen(false);
      setSelectedData({});
    } else {
      toast.error("Error Admitting Client to Bed");
    }
  };

  const onHide = () => {
    setNewBedOpen(false);
    setBedData({ name: "" });
  };

  return (
    <>
      <Modal show={newBedOpen} onHide={onHide} animation={false}>
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>{bedData.id ? "Edit Bed" : "Add Bed"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          <FloatingInput
            type="text"
            name="name"
            label={<div className="text-danger">Bed Name</div>}
            value={bedData.name}
            onChange={(e) => setBedData({ ...bedData, name: e.target.value })}
          />
          <FloatingInput
            type="date"
            name="effective"
            label={<div className="text-danger">Effective Date</div>}
            value={bedData.effective || ""}
            onChange={(e) =>
              setBedData({ ...bedData, effective: e.target.value })
            }
          />
          {data.type === "FosterHome" && (
            <div>
              <FloatingInput
                type="select"
                name="bedType"
                label="Bed Type"
                options={[
                  {
                    _id: "Day Only, No Overnight",
                    name: "Day Only, No Overnight",
                  },
                  { _id: "On Hold", name: "On Hold" },
                  { _id: "Respite Only", name: "Respite Only" },
                  { _id: "Sibling Set Only", name: "Sibling Set Only" },
                  { _id: "Standard", name: "Standard" },
                ]}
                value={bedData.bedType}
                onChange={(e) =>
                  setBedData({ ...bedData, bedType: e.target.value })
                }
              />
              {bedData.bedType === "On Hold" && (
                <FloatingInput
                  type="select"
                  name="onHoldReason"
                  label="On Hold Reason"
                  options={[
                    {
                      _id: "Pending Investigation",
                      name: "Pending Investigation",
                    },
                    {
                      _id: "Respite Only-Pending Investigation",
                      name: "Respite Only-Pending Investigation",
                    },
                    {
                      _id: "Ongoing Investigation",
                      name: "Ongoing Investigation",
                    },
                    {
                      _id: "Respite Only-Ongoing Investigation",
                      name: "Respite Only-Ongoing Investigation",
                    },
                    {
                      _id: "Bed Hold-Ongoing Investigation",
                      name: "Bed Hold-Ongoing Investigation",
                    },
                    {
                      _id: "Heightened Needs of Other Home Beds",
                      name: "Heightened Needs of Other Home Beds",
                    },
                    { _id: "FP Requested Hold", name: "FP Requested Hold" },
                    { _id: "Other", name: "Other" },
                  ]}
                  value={bedData.onHoldReason}
                  onChange={(e) =>
                    setBedData({ ...bedData, onHoldReason: e.target.value })
                  }
                />
              )}
              <FloatingInput
                type="select"
                name="gender"
                label="Gender"
                options={[
                  { _id: "male", name: "Male" },
                  { _id: "female", name: "Female" },
                  { _id: "any", name: "Any" },
                ]}
                value={bedData.gender}
                onChange={(e) =>
                  setBedData({ ...bedData, gender: e.target.value })
                }
              />
              <FloatingInput
                type="select"
                name="ageMin"
                label="Age Min"
                value={bedData.ageMin}
                options={Array.from({ length: 19 }, (_, i) => ({
                  _id: i,
                  name: i.toString(),
                }))}
                onChange={(e) =>
                  setBedData({ ...bedData, ageMin: e.target.value })
                }
              />
              <FloatingInput
                type="select"
                name="ageMax"
                label="Age Max"
                value={bedData.ageMax}
                options={Array.from({ length: 19 }, (_, i) => ({
                  _id: i,
                  name: i.toString(),
                }))}
                onChange={(e) =>
                  setBedData({ ...bedData, ageMax: e.target.value })
                }
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-safy-info text-white">
          <button
            className="border border-dark btn btn-danger"
            onClick={onHide}
          >
            Cancel
          </button>
          <button
            className="border border-dark btn btn-success"
            onClick={addBed}
            disabled={
              !bedData.name ||
              !bedData.effective ||
              (bedData.bedType === "On Hold" && !bedData.onHoldReason)
            }
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={newBedAssignmentOpen}
        onHide={onHide}
        animation={false}
        size="xl"
      >
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>Add Bed Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          {data.companyName && selectedData?.client == undefined && (
            <ClientSearch
              hideNewClient={true}
              state={data.companyName}
              setClient={(e) => {
                let obj = {
                  ...selectedData,
                  client: e,
                };
                if (
                  obj.client?.programs?.filter(
                    (cp) => !cp.name.includes("Foster Homes") && !cp.discharge
                  ).length === 1
                ) {
                  obj.clientProgram = obj.client.programs[0].id;
                  obj.clientProgramName = obj.client.programs[0].name;
                  obj.unit = obj.client.programs[0].unit?.id;
                  obj.unitName = obj.client.programs[0].unit?.name;
                }
                setSelectedData(obj);
              }}
              setShow={() => console.log(`Selected Client`)}
            />
          )}
          {selectedData?.client && (
            <>
              <div className="h5 mb-4">{selectedData?.client?.fullname}</div>
              <FloatingInput
                className="mb-4"
                type="select"
                name="clientProgram"
                label="Client Program"
                options={selectedData.client.programs
                  .filter(
                    (cp) => !cp.name.includes("Foster Homes") && !cp.discharge
                  )
                  .map((program) => ({
                    _id: program.id,
                    name: `${program.name} (${DateTime.fromISO(
                      program.admission
                    ).toLocaleString(DateTime.DATE_SHORT)})`,
                    unitId: program.name,
                    unitName: program.name,
                  }))}
                value={selectedData?.clientProgram}
                onChange={(e) => {
                  // console.log(
                  //   e.target.options[e.target.selectedIndex].getAttribute(
                  //     "unitId"
                  //   )
                  // );
                  setSelectedData({
                    ...selectedData,
                    clientProgram: e.target.value,
                    clientProgramName:
                      e.target.options[e.target.selectedIndex].text,
                    unit: selectedData.client.programs.find(
                      (program) => program.id === e.target.value
                    )?.unit?.id,
                    unitName: selectedData.client.programs.find(
                      (program) => program.id === e.target.value
                    )?.unit?.name,
                  });
                }}
              />
              <FloatingInput
                className="mb-4"
                type="date"
                name="admissionDate"
                label="Admission Date"
                value={selectedData.admissionDate || ""}
                onChange={(e) =>
                  setSelectedData({
                    ...selectedData,
                    admissionDate: e.target.value,
                  })
                }
              />
              <FloatingInput
                className="mb-4"
                type="select"
                name="serviceConfiguration"
                label="Service Configuration"
                options={serviceConfigs
                  .filter(
                    (sc) =>
                      new Date(sc.effective) <=
                        new Date(selectedData.admissionDate) &&
                      (new Date(selectedData.admissionDate) <= sc.expiration
                        ? new Date(sc.expiration)
                        : true)
                  )
                  .map((sc) => ({
                    _id: sc.id,
                    name: sc.name,
                  }))}
                value={selectedData?.serviceConfiguration}
                onChange={(e) =>
                  setSelectedData({
                    ...selectedData,
                    serviceConfiguration: e.target.value,
                    serviceConfigurationName:
                      e.target.options[e.target.selectedIndex].text,
                  })
                }
              />
            </>
          )}
          {selectedData.serviceConfiguration && (
            <FloatingInput
              className="mb-4"
              type="select"
              name="serviceStatus"
              label="Service Status"
              options={serviceConfigs
                .find((sc) => sc.id === selectedData.serviceConfiguration)
                .configuration.serviceStatuses.map((ss) => ({
                  _id: ss,
                  name: ss,
                }))}
              value={selectedData.serviceStatus}
              onChange={(e) =>
                setSelectedData({
                  ...selectedData,
                  serviceStatus: e.target.value,
                })
              }
            />
          )}
        </Modal.Body>
        <Modal.Footer className="bg-safy-info text-white">
          <button
            className="btn btn-danger"
            onClick={() => {
              setNewBedAssignmentOpen(false);
              setSelectedData({});
            }}
          >
            Close
          </button>
          <button
            className="btn btn-success"
            onClick={() => addBedAssignment()}
            disabled={
              !selectedData.client ||
              !selectedData.serviceConfiguration ||
              !selectedData.admissionDate ||
              !selectedData.serviceStatus
            }
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={bedStatusOpen}
        onHide={() => {
          setBedStatusOpen(false);
          setBedStatusData({});
        }}
        animation={false}
        size="xl"
      >
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>{`${
            !bedStatusData.expiration ? "Deactivate" : "Activate"
          } - ${bedStatusData.name}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <FloatingInput
              type="date"
              name="statusDate"
              label="Date"
              value={bedStatusData.statusDate || ""}
              onChange={(e) =>
                setBedStatusData({
                  ...bedStatusData,
                  statusDate: e.target.value,
                })
              }
            />
            {!bedStatusData.expiration && (
              <FloatingInput
                type="select"
                name="statusReason"
                label="Reason"
                value={bedStatusData.statusReason || ""}
                options={[
                  { _id: "Deceased", name: "Deceased" },
                  { _id: "Entry Error", name: "Entry Error" },
                  { _id: "Not Applicable", name: "Not Applicable" },
                ]}
                onChange={(e) =>
                  setBedStatusData({
                    ...bedStatusData,
                    statusReason: e.target.value,
                  })
                }
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-safy-info text-white">
          <button
            className="btn btn-success"
            onClick={() => {
              toggleBedStatus(bedStatusData);
            }}
            disabled={
              !bedStatusData.statusDate ||
              (!bedStatusData.expiration && !bedStatusData.statusReason)
            }
          >
            Save
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              setBedStatusOpen(false);
              setBedStatusData({});
            }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
      <div className="mb-4">
        <div className="row mb-2">
          <div className="col-10 h3 mt-2 g-4">Beds</div>
          <div className="col-2">
            {data.type === "FosterHome" && (
              <div
                className="btn btn-primary float-end"
                role="button"
                onClick={() => setNewBedOpen(true)}
              >
                Add Bed
              </div>
            )}
            {data.type !== "FosterHome" && (
              <div
                className="btn btn-primary float-end"
                role="button"
                onClick={() => addProgramBed()}
              >
                Add Program Bed
              </div>
            )}
          </div>
        </div>
        {beds.map((bed, index) => (
          <div
            key={index}
            className={`${
              bed.active === false ||
              bed.expiration ||
              bed.configuration.bedType === "On Hold"
                ? "bg-danger"
                : bed.clientHistory?.filter(
                    (c) =>
                      c.active &&
                      c.admission &&
                      (!c.discharge || c.discharge === "")
                  ).length > 0
                ? "bg-secondary"
                : "bg-success"
            }  card-safy rounded text-white p-2 mb-2`}
            title={`${
              bed.effective
                ? DateTime.fromISO(bed.effective).toLocaleString()
                : "Unknown"
            } - ${
              bed.expiration
                ? DateTime.fromISO(bed.expiration).toLocaleString()
                : "Present"
            }`}
          >
            <div className="row">
              <div className="col">{`${bed.name} ${
                bed.active === false ? "(Inactive)" : ""
              }${bed.expiration ? "(Expired)" : ""}`}</div>

              <div className="col">
                {data.type === "FosterHome" && (
                  <div>{`Gender: ${bed.configuration.gender || ""}`}</div>
                )}
              </div>
              <div className="col">
                {data.type === "FosterHome" && (
                  <div>{`Ages: ${bed.configuration.ageMin || ""} - ${
                    bed.configuration.ageMax || ""
                  }`}</div>
                )}
              </div>
              <div className="col" title={bed.configuration.onHoldReason || ""}>
                {data.type === "FosterHome" && (
                  <div>Type: {bed.configuration.bedType || ""}</div>
                )}
              </div>
              <div className="col">
                {bed.clientHistory.filter(
                  (c) => c.admission && (!c.discharge || c.discharge === "")
                ).length === 0 && (
                  <i
                    className={`rounded border border-dark fas ${
                      bed.expiration
                        ? "fa-toggle-off text-danger"
                        : "fa-toggle-on text-success"
                    } bg-white p-2 me-2 mx-1 float-end`}
                    role="button"
                    title={bed.expiration ? "Reactivate Bed" : "Deactivate Bed"}
                    onClick={() => {
                      if (bed.expiration) {
                        toggleBedStatus(bed);
                      } else {
                        setBedStatusData(bed);
                        setBedStatusOpen(true);
                      }
                    }}
                  ></i>
                )}
                {bed.active && (
                  <i
                    className="rounded border border-dark fas fa-edit bg-white text-primary p-2 mx-1 float-end"
                    role="button"
                    onClick={() => {
                      setBedData({
                        id: bed.id,
                        name: bed.name,
                        effective: bed.effective,
                        bedType: bed.configuration.bedType || "",
                        gender: bed.configuration.gender || "",
                        ageMin: bed.configuration.ageMin || "",
                        ageMax: bed.configuration.ageMax || "",
                      });
                      setNewBedOpen(true);
                    }}
                  ></i>
                )}
                {bed.active &&
                  bed.clientHistory.filter(
                    (c) => c.admission && (!c.discharge || c.discharge === "")
                  ).length === 0 && (
                    <i
                      className="rounded border border-dark fas fa-user-plus bg-white text-dark p-2 mx-1 float-end"
                      role="button"
                      onClick={() => {
                        setNewBedAssignmentOpen(true);
                        setSelectedData({
                          ...selectedData,
                          bed: bed,
                        });
                      }}
                    ></i>
                  )}
              </div>
            </div>
            <div className="fw-bold">{JSON.stringify(bed.clientHisory)}</div>
            <BedClientHistory
              bed={bed}
              data={data}
              setData={setData}
              updateData={updateData}
            />
          </div>
        ))}
      </div>
    </>
  );
}

export default ResidenceBeds;
